// Dossiers côté usager (brief L1-A) : brouillon, dépôt, suivi, complément, retrait, documents.
// La fiche usager ne contient jamais les notes internes ni les événements internes de validation :
// seulement les statuts usager successifs, le motif d'un complément demandé et la décision.
import { z } from 'zod';
import { schemaCodeDemarche } from './demarches-configuration';
import { schemaCodeCollectivite } from './usagers-comptes';

const uuid = z.string().uuid('Identifiant invalide');
const codePiece = z.string().regex(/^[a-z][a-z0-9_]{1,62}$/, 'Code de type de pièce invalide');
const donnees = z.record(z.string().regex(/^[a-z][a-z0-9_]{0,62}$/), z.unknown());

export const schemaPieceDuCoffre = z.strictObject({
  piece_id: uuid,
  /** Type de pièce attendu par la démarche ; par défaut le type de la pièce du coffre. */
  type: codePiece.optional(),
});

export const schemaCreationDossierUsager = z.strictObject({
  collectivite: schemaCodeCollectivite,
  demarche: schemaCodeDemarche,
  /** Dépôt au nom d'une entité représentée (représentation validée dans cette collectivité). */
  representation_id: uuid.optional(),
  donnees_formulaire: donnees.default({}),
  pieces_coffre: z.array(schemaPieceDuCoffre).max(30).default([]),
});

export const schemaModificationDossierUsager = z.strictObject({
  donnees_formulaire: donnees.optional(),
  pieces_coffre: z.array(schemaPieceDuCoffre).max(30).default([]),
});

export const schemaComplementUsager = z.strictObject({
  /** Réponses : champs du formulaire complétés ou corrigés. */
  reponses: donnees.default({}),
  pieces_coffre: z.array(schemaPieceDuCoffre).max(30).default([]),
  message: z.string().trim().max(2000).optional(),
});

export const schemaRetraitUsager = z.strictObject({ motif: z.string().trim().min(3, 'Motif obligatoire').max(1000) });

/** Champs texte du formulaire multipart de POST /usager/dossiers/:id/pieces (fichier « fichier »). */
export const schemaAjoutPieceUsager = z.strictObject({ type: codePiece, titre: z.string().trim().min(1).max(300).optional() });

export const schemaParametresDocumentUsager = z.object({ id: uuid, docId: uuid });

export const ACTIONS_USAGER_DOSSIER = ['modifier', 'deposer', 'completer', 'retirer'] as const;

// Réponses -----------------------------------------------------------------------------------

export const schemaResumeDossierUsager = z.object({
  id: uuid,
  numero: z.string().nullable(),
  collectivite: z.object({ code: z.string(), nom: z.string() }),
  demarche: z.object({ code: z.string(), libelle: z.string(), numero_version: z.number() }),
  statut: z.object({ code: z.string(), libelle: z.string(), texte: z.string().nullable() }),
  representation: z.object({ id: uuid, entite_nom: z.string() }).nullable(),
  cree_le: z.string(),
  enregistre_le: z.string().nullable(),
  echeance_reglementaire: z.string().nullable(),
});

/** Message de la conversation usager d'un dossier (messagerie, H8-01) ; jamais une note interne. */
export const schemaMessageConversationDossier = z.object({
  id: uuid,
  auteur: z.object({ compte_id: uuid, nom: z.string().nullable(), cote: z.enum(['usager', 'collectivite']) }),
  contenu: z.string(),
  horodatage: z.string(),
  pieces: z.array(z.object({ document_id: uuid, titre: z.string(), type_mime: z.string(), taille: z.number() })),
});
export type MessageConversationDossier = z.infer<typeof schemaMessageConversationDossier>;

/** État de la conversation d'un dossier : ouverte au dépôt en ligne, archivée à la clôture, absente avant le dépôt. */
export const schemaConversationDossier = z.object({
  statut: z.enum(['active', 'archivee', 'aucune']),
  conversation_id: uuid.nullable(),
  non_lus: z.number(),
  messages: z.array(schemaMessageConversationDossier),
});
export type ConversationDossier = z.infer<typeof schemaConversationDossier>;

export const schemaFicheDossierUsager = schemaResumeDossierUsager.extend({
  resultat: z.enum(['approuve', 'approuve_sous_conditions', 'refuse']).nullable(),
  motif_decision: z.string().nullable(),
  donnees_formulaire: z.record(z.string(), z.unknown()),
  pieces: z.array(z.object({ id: uuid, type: z.string(), titre: z.string(), document_id: uuid, verdict: z.string(), motif: z.string().nullable() })),
  pieces_attendues: z.array(z.object({ type: z.string(), libelle: z.string(), obligatoire: z.boolean(), fournie: z.boolean() })),
  complement: z.object({ motif: z.string().nullable(), demande_le: z.string(), relance_le: z.string().nullable() }).nullable(),
  documents_delivres: z.array(z.object({ id: uuid, titre: z.string(), code_verification: z.string().nullable(), cree_le: z.string() })),
  historique: z.array(z.object({ statut: z.string(), libelle: z.string(), date: z.string() })),
  conversation: schemaConversationDossier,
  actions_possibles: z.array(z.enum(ACTIONS_USAGER_DOSSIER)),
});
export type FicheDossierUsager = z.infer<typeof schemaFicheDossierUsager>;
